import React, { useState, useEffect } from 'react';
import { getUserHistory, deleteHistoryItem, toggleFavorite } from '../services/historyService';
import { useAuth } from '../contexts/AuthContext';
import { canUseFavourites } from '../services/planGatingService';
import VideoGrid from '../components/VideoGrid';
import UpgradeModal from '../components/UpgradeModal';
import { AlertTriangle } from 'react-feather';

const FoldersPage = () => {
  const { currentUser } = useAuth();
  const [videos, setVideos] = useState([]);
  const [allVideos, setAllVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favouritesEnabled, setFavouritesEnabled] = useState(false);
  const [error, setError] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [dismissedWarning, setDismissedWarning] = useState(false);

  useEffect(() => {
    if (currentUser) {
      loadVideos();
      checkFavouritesAccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const checkFavouritesAccess = async () => {
    if (currentUser?.uid) {
      try {
        const check = await canUseFavourites(currentUser.uid);
        setFavouritesEnabled(check.allowed);
      } catch (err) {
        console.error('Error checking favourites access:', err);
        setFavouritesEnabled(false);
      }
    }
  };

  const loadVideos = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      const userHistory = await getUserHistory(currentUser.uid, 100);
      const allVids = Array.isArray(userHistory) ? userHistory : [];
      setAllVideos(allVids);
      // Filter to only show favorited videos
      const favorited = allVids.filter(video => video.isFavorite === true);
      setVideos(favorited);
    } catch (err) {
      console.error('Failed to load videos:', err);
      setVideos([]);
      setAllVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFavorite = async (videoId, currentFavoriteStatus, e) => {
    e.stopPropagation();

    // Check if user can use favourites feature
    if (currentUser?.uid) {
      try {
        const favouritesCheck = await canUseFavourites(currentUser.uid);
        if (!favouritesCheck.allowed) {
          setError(favouritesCheck.error || 'Favourites are not available on your current plan. Upgrade to Pro or Growth to unlock this feature.');
          setShowUpgradeModal(true);
          return;
        }
      } catch (checkError) {
        console.error('Error checking favourites access:', checkError);
        setError('Failed to verify plan limits. Please try again.');
        return;
      }
    }

    const newFavoriteStatus = !currentFavoriteStatus;

    try {
      await toggleFavorite(videoId, newFavoriteStatus);
      // Update in local state
      setAllVideos(allVideos.map(v => 
        v.id === videoId ? { ...v, isFavorite: newFavoriteStatus } : v
      ));
      // Update favorites list
      if (newFavoriteStatus) {
        // Add to favorites if not already there
        const video = allVideos.find(v => v.id === videoId);
        if (video && !videos.find(v => v.id === videoId)) {
          setVideos([...videos, { ...video, isFavorite: true }]);
        }
      } else {
        // Remove from favorites
        setVideos(videos.filter(v => v.id !== videoId));
      }
      setError(null);
    } catch (err) {
      console.error('Error toggling favorite:', err);
      setError(err.message || 'Failed to update favorite status');
    }
  };

  const handleDelete = async (videoId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this video?')) {
      return;
    }

    try {
      await deleteHistoryItem(videoId, currentUser?.uid);
      setVideos(videos.filter(v => v.id !== videoId));
      setAllVideos(allVideos.filter(v => v.id !== videoId));
      setError(null);
    } catch (err) {
      console.error('Error deleting video:', err);
      setError(err.message || 'Failed to delete video');
    }
  };

  const handleSelectVideo = (videoId, e) => {
    e.stopPropagation();
    // Selection functionality not needed for favourites page
  };

  return (
    <div style={{
      fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
      background: 'linear-gradient(135deg, #1a1b2e 0%, #2d1b3d 100%)',
      color: '#ffffff',
      minHeight: '100vh',
      padding: '30px',
    }}>
      {/* Warning Message if not enabled */}
      {!favouritesEnabled && !dismissedWarning && (
        <div style={{
          padding: '16px',
          backgroundColor: '#fef3c7', // warning.light
          border: '1px solid #f59e0b', // warning.base
          borderRadius: '8px', // borderRadius.md
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <AlertTriangle size={20} color="#ffc107" />
            <p style={{
              fontSize: '14px',
              color: '#ffc107',
              margin: 0,
            }}>
              Favourites are only available on Pro and Growth plans. Upgrade to unlock this feature.
            </p>
          </div>
          <button
            onClick={() => setDismissedWarning(true)}
            style={{
              background: 'none',
              border: 'none',
              color: '#ffc107',
              cursor: 'pointer',
              fontSize: '18px',
              padding: '0 8px',
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fee2e2', // error.light
          border: '1px solid #ef4444', // error.base
          borderRadius: '8px',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span style={{ color: '#ffcdd2' }}>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#ffcdd2',
              cursor: 'pointer',
              fontSize: '18px',
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div style={{
        marginBottom: '32px',
      }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: 600,
          marginBottom: '8px',
          color: 'white',
        }}>
          Favourites
        </h1>
        <p style={{
          fontSize: '14px',
          color: 'rgba(255, 255, 255, 0.7)',
        }}>
          {videos.length} favorite{videos.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Video Grid */}
      {loading ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '64px',
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid rgba(255, 255, 255, 0.2)',
            borderTopColor: '#6C5CE7',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}></div>
          <p style={{
            marginTop: '16px',
            color: 'rgba(255, 255, 255, 0.7)',
          }}>
            Loading favorites...
          </p>
        </div>
      ) : videos.length === 0 ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '64px',
          textAlign: 'center',
        }}>
          <p style={{
            fontSize: '16px',
            color: 'rgba(255, 255, 255, 0.7)',
            marginBottom: '8px',
          }}>
            No favorite videos yet.
          </p>
          <p style={{
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.5)',
          }}>
            Add videos to favorites by clicking the star icon on any video!
          </p>
        </div>
      ) : (
        <VideoGrid
          videos={videos}
          onToggleFavorite={handleToggleFavorite}
          onDelete={handleDelete}
          onSelectVideo={handleSelectVideo}
          selectedVideos={[]}
          showCheckbox={false}
          showFavorite={true}
          showDelete={true}
          canFavorite={favouritesEnabled}
        />
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default FoldersPage;
